import React, { useEffect, useState } from 'react';
import css from './PatientInfo.module.css';
import {
  ErrorBanner,
  H2,
  HelplinePhoneNumber,
  IndicatesRequired,
  Paragraph,
  PhoneLink,
  RadioCardGroup,
  RadioCardOption,
  RadioCardSizing,
  StackedColumn,
  StackedColumnSpacing,
} from '../../../../../core';
import {
  Demographics,
  EmptyDemographics,
  HaveMRN,
  PatientDetails,
  PreviouslySeen,
  SchedulingTarget,
} from '../../../../../api/models';
import ReCAPTCHA from 'react-google-recaptcha';
import {
  ReCaptchaState,
  ReCaptchaValidator,
} from '../../../../reCaptchaValidator';
import { DemographicsInput } from '../../../../demographicsInput';
import { ExistingPatient } from './existingPatient';

export interface PatientInfoProps {
  validate: boolean;
  patientDetails?: PatientDetails;
  showError?: boolean;
  onChangePatientDetails: (patientDetails: PatientDetails) => void;
  reCaptchaRef: React.RefObject<ReCAPTCHA>;
  reCaptchaState: ReCaptchaState;
  onReCaptchaChange: () => void;
}

const targetOptions: RadioCardOption[] = [
  {
    value: SchedulingTarget.Myself,
    label: `Myself`,
  },
  {
    value: SchedulingTarget.SomeoneElse,
    label: `Someone else`,
  },
];

const errorContent = (
  <StackedColumn>
    <Paragraph text="We couldn't verify the information entered below." />
    <div>
      <Paragraph text="Review the information to make sure it's correct, or contact the Marshfield Clinic Health System HelpLine at: " />
      <PhoneLink phoneNumber={HelplinePhoneNumber} includeImage={false} />
    </div>
  </StackedColumn>
);

const PatientInfo: React.FC<PatientInfoProps> = (props: PatientInfoProps) => {
  const [target, setTarget] = useState<SchedulingTarget>();
  const [demographics, setDemographics] =
    useState<Demographics>(EmptyDemographics);

  useEffect(() => {
    setTarget(props.patientDetails?.target);
    setDemographics(props.patientDetails?.demographics ?? EmptyDemographics);
  }, [props.patientDetails]);

  const onChangeTarget = (selectedValue: string) => {
    const selectedTargetValue = selectedValue as SchedulingTarget;
    if (selectedTargetValue) {
      setTarget(selectedTargetValue);
      props.onChangePatientDetails({
        ...(props.patientDetails ?? {}),
        target: selectedTargetValue,
      });
    }
  };

  const onChangeDemographics = (demographics: Demographics) => {
    setDemographics(demographics);
    props.onChangePatientDetails({
      ...(props.patientDetails ?? {}),
      demographics: demographics,
    });
  };

  return (
    <StackedColumn fillHeight={true} spacing={StackedColumnSpacing.Wide}>
      <StackedColumn>
        <H2 text="Please fill out information about the patient." />
        <IndicatesRequired />
      </StackedColumn>
      {props.showError ? (
        <ErrorBanner title="Please try again." content={errorContent} />
      ) : null}
      <div className={css.questionContainer}>
        <StackedColumn spacing={StackedColumnSpacing.Wider}>
          <RadioCardGroup
            label="Who are you scheduling the appointment for?"
            required={true}
            validate={props.validate}
            groupName="schedulingTarget"
            sizing={RadioCardSizing.Small}
            fullWidth={true}
            radioOptions={targetOptions}
            selectedValue={target ?? ''}
            onChange={onChangeTarget}
          />
          {target ? (
            <ExistingPatient
              patientDetails={props.patientDetails}
              onChangePatientDetails={props.onChangePatientDetails}
              validate={props.validate}
            />
          ) : null}
          {target &&
          (props.patientDetails?.previouslySeen === PreviouslySeen.No ||
            props.patientDetails?.previouslySeen === PreviouslySeen.NotSure ||
            props.patientDetails?.haveMRN === HaveMRN.No) ? (
            <DemographicsInput
              target={target}
              demographics={demographics}
              validate={props.validate}
              onDemographicsChanged={onChangeDemographics}
            />
          ) : null}
          <ReCaptchaValidator
            reCaptchaRef={props.reCaptchaRef}
            reCaptchaState={props.reCaptchaState}
            onReCaptchaChange={props.onReCaptchaChange}
          />
        </StackedColumn>
      </div>
    </StackedColumn>
  );
};

export default PatientInfo;
